/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, ReactNode } from "react";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Input from "components/Input";
import Select from "react-select";
import apiService from "services/apiService";
import Button from "components/Button";
import Loader from "components/Loader";
import { sortByKey } from "util/sortArrByName";
import { validateNoTrailingOrLeadingWhitespace } from "util/validation";
// NOTE: shared resolver so a string `response.data` and an object
// `response.data` both end up as a readable toast message.
import { getApiErrorMessage } from "util/errorMessage";
import { handleNameInput } from "util/Util";
import { customSelectStyles } from "util/selectStyles";
import { UserRole } from "../../util/roles";
import PhoneNumberInput from "components/PhoneNumberInput";
import {
  DEFAULT_COUNTRY_CODE,
  joinPhoneNumber,
  phoneNumberSchema,
  splitPhoneNumber,
} from "util/phoneCountry";

const schema = yup.object().shape({
  emails: yup.string().required("Employees are required"),
  clientContactName: validateNoTrailingOrLeadingWhitespace("Client Name"),
  clientContactNumber: phoneNumberSchema("clientContactCountryCode", {
    requiredMessage: "Client Mobile Number is Required",
  }),
  clientContactCountryCode: yup.string().optional().default(DEFAULT_COUNTRY_CODE),
  manager: yup.string().required("Manager is Required"),
  name: validateNoTrailingOrLeadingWhitespace("Name").matches(
    /^[a-zA-Z0-9 ]*$/,
    "Special characters are not allowed"
  ),
  projectDetail: validateNoTrailingOrLeadingWhitespace("Project Details").max(
    1000,
    "Project Details cannot exceed 1000 characters"
  ),
  startDate: yup.string().required("Start Date is Required"),
});

interface Props {
  onClose: () => void;
  data: any;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

interface OptionProps {
  value: string;
  label: string;
  role?: number;
}

type UserOptionSource = { email?: string; displayName?: string; role?: number };

// CHANGE: replaced the hand-rolled resolver (duplicated verbatim in AddProject)
// with the shared `getApiErrorMessage`, which type-checks the payload and also
// reads `error` / `details` and Spring validation lists. Behaviour kept: a 500
// still shows the generic text instead of the raw server body.
const PROJECT_UPDATE_ERROR_FALLBACK = "Failed to update project";

const getProjectUpdateErrorMessage = (error: unknown) =>
  getApiErrorMessage(error, PROJECT_UPDATE_ERROR_FALLBACK, { useFallbackOnServerError: true });

const formatDateForInput = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

export default function EditProject({ onClose, data, onSuccess, onError }: Props) {
  const methods = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      ...data,
      clientContactNumber: splitPhoneNumber(data.clientContactNumber).nationalNumber,
      clientContactCountryCode: splitPhoneNumber(data.clientContactNumber).countryCode,
      startDate: formatDateForInput(data.startDate),
      completionDate: formatDateForInput(data.completionDate),
      emails: typeof data.emails === "string" ? data.emails : "",
      manager: data.manager ? String(data.manager) : "",
    },
  });
  const { isDirty, isSubmitting, dirtyFields } = methods.formState;

  const [loading, setLoading] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [options, setOptions] = useState<OptionProps[]>([]);

  // Master dictionary to ensure selected values ALWAYS map to a label
  const [knownUsers, setKnownUsers] = useState<Map<string, OptionProps>>(() => {
    const initialMap = new Map<string, OptionProps>();

    // Seed with initial fallbacks so the UI doesn't crash while fetching deep pages
    if (data.manager) {
      initialMap.set(String(data.manager), { value: String(data.manager), label: String(data.manager), role: 2 });
    }
    if (typeof data.emails === "string" && data.emails) {
      data.emails.split(",").forEach((e: string) => {
        const email = e.trim();
        if (email) initialMap.set(email, { value: email, label: email });
      });
    }
    return initialMap;
  });

  const [userPage, setUserPage] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const USER_PAGE_SIZE = 10;

  const getUsersFromPayload = (payload: unknown): UserOptionSource[] => {
    if (Array.isArray(payload)) return payload as UserOptionSource[];
    if (typeof payload === "object" && payload !== null && "content" in payload && Array.isArray((payload as any).content)) {
      return (payload as { content: UserOptionSource[] }).content;
    }
    return [];
  };

  const dedupeAndSort = (items: OptionProps[]) => {
    const dedup = new Map<string, OptionProps>();
    items.forEach((item) => {
      const existing = dedup.get(item.value);
      // Upgrade fallback email/IDs to actual display names whenever found
      if (!existing) {
        dedup.set(item.value, item);
      } else if (existing.label === existing.value && item.label !== item.value) {
        dedup.set(item.value, item);
      }
    });
    return sortByKey(Array.from(dedup.values()), "label");
  };

  // ✅ TARGETED PRE-FETCH FOR OFF-PAGE USERS
  useEffect(() => {
    const fetchPreselectedUsers = async () => {
      const managerId = data.manager ? String(data.manager).trim() : "";
      const employeeIds = typeof data.emails === "string"
        ? data.emails.split(",").map((e: string) => e.trim()).filter(Boolean)
        : [];

      const uniqueSelectedIds = Array.from(new Set([managerId, ...employeeIds].filter(Boolean)));

      if (uniqueSelectedIds.length === 0) return;

      try {
        // Query the API for the specific records to bypass pagination boundaries
        // Note: Change to an exact ID endpoint if your API supports bulk lookups (e.g., api/users/bulk?ids=a,b,c)
        const userPromises = uniqueSelectedIds.map(id =>
          apiService.get(`api/users?search=${encodeURIComponent(id)}&size=10`)
        );

        const responses = await Promise.allSettled(userPromises);
        const resolvedUsers: OptionProps[] = [];

        responses.forEach(res => {
          if (res.status === "fulfilled") {
            const fetched = getUsersFromPayload(res.value.data);

            // Extract and map the precise user match
            const exactMatch = fetched.find((u: any) => uniqueSelectedIds.includes(u?.email));
            if (exactMatch && exactMatch.email) {
              resolvedUsers.push({
                value: String(exactMatch.email),
                label: String(exactMatch.displayName || exactMatch.email),
                role: exactMatch.role
              });
            }
          }
        });

        // Merge fetched actual details back into knownUsers & options list
        if (resolvedUsers.length > 0) {
          setKnownUsers(prev => {
            const next = new Map(prev);
            resolvedUsers.forEach(u => next.set(u.value, u));
            return next;
          });

          setOptions(prev => dedupeAndSort([...prev, ...resolvedUsers]));
        }
      } catch (err) {
        console.error("Failed to fetch exact preselected user details:", err);
      }
    };

    fetchPreselectedUsers();
  }, [data.manager, data.emails]);

  const onSubmit: SubmitHandler<any> = (formData) => {
    // Dynamically calculate the team size based on selected employee emails
    const employeeCount = formData.emails
      ? formData.emails.split(",").filter((e: string) => e.trim()).length
      : 0;

    const { clientContactCountryCode, ...projectValues } = formData;
    const cleanedData = {
      ...projectValues,
      clientContactNumber: joinPhoneNumber(clientContactCountryCode, formData.clientContactNumber),
      teamSize: employeeCount, // Override old default with accurately calculated size
      projectDetail: formData.projectDetail?.trim(),
    };

    apiService
      .put("api/project", cleanedData)
      .then((res) => {
        if (res.status === 200) {
          onSuccess("Project is Updated successfully");
          onClose();
        } else {
          // NOTE: apiService sets no `validateStatus`, so axios resolves ONLY on
          // 2xx — every real failure goes to `.catch()`. This branch is reached
          // just by a non-200 success (201/204), where `res.data` is the updated
          // record, not an error. Running it through the error resolver would
          // surface the status text ("Created") as an error toast, so the
          // generic message is used, exactly as before.
          onError(PROJECT_UPDATE_ERROR_FALLBACK);
        }
      })
      .catch((error) => onError(getProjectUpdateErrorMessage(error)));
  };

  const fetchUsers = async (page: number, searchValue: string, append: boolean, showPageLoader = false) => {
    if (loadingUsers) return;

    setLoadingUsers(true);
    if (showPageLoader) setLoading(true);

    try {
      const query = new URLSearchParams();
      query.set("page", String(page));
      query.set("size", String(USER_PAGE_SIZE));
      if (searchValue.trim()) query.set("search", searchValue.trim());

      const res = await apiService.get(`api/users?${query.toString()}`);
      const payload = res.data;
      const users = getUsersFromPayload(payload);

      const mapped: OptionProps[] = users.map((u: any) => ({
        value: String(u?.email || ""),
        label: String(u?.displayName || ""),
        role: u?.role,
      }));

      setOptions((prev) => dedupeAndSort(append ? [...prev, ...mapped] : mapped));

      setKnownUsers((prev) => {
        const next = new Map(prev);
        mapped.forEach(opt => {
          const existing = next.get(opt.value);
          if (!existing || (existing.label === existing.value && opt.label !== opt.value)) {
            next.set(opt.value, opt);
          }
        });
        return next;
      });

      setUserPage(page);
      if (typeof payload?.last === "boolean") {
        setHasMoreUsers(!payload.last);
      } else if (typeof payload?.totalPages === "number") {
        setHasMoreUsers(page < payload.totalPages - 1);
      } else {
        setHasMoreUsers(mapped.length === USER_PAGE_SIZE);
      }
    } catch {
      if (!append) setOptions([]);
    } finally {
      setLoadingUsers(false);
      if (showPageLoader) setLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void fetchUsers(0, userSearch, false, loading);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [userSearch]);

  // Dynamically resolve react-select values against the verified knownUsers dictionary
  const watchedManager = methods.watch("manager");
  const watchedEmails = methods.watch("emails");
  const watchedStartDate = methods.watch("startDate");

  const managerValueOption = watchedManager
    ? (knownUsers.get(watchedManager) || { value: watchedManager, label: watchedManager, role: 2 })
    : null;

  const employeeValueOptions = (typeof watchedEmails === "string" && watchedEmails)
    ? watchedEmails.split(",").map(e => e.trim()).filter(Boolean).map(email => knownUsers.get(email) || { value: email, label: email })
    : [];

  const managerSelectOptions = dedupeAndSort([
    ...(managerValueOption ? [managerValueOption] : []),
    ...options.filter((opt) => opt.role === UserRole.MANAGER || UserRole.ADMIN)
  ]);

  const employeeSelectOptions = dedupeAndSort([
    ...employeeValueOptions,
    ...options
  ]);

  return (
    <FormProvider {...methods}>
      {loading ? (
        <div className="min-h-[420px] flex items-center justify-center">
          <Loader />
        </div>
      ) : (
        <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
          <div className="grid grid-cols-2 gap-x-3 gap-y-2 px-4 pt-3">
            <Input compact name="name" label="Project Name" required />

            {/* Manager */}
            <div>
              <label className="block mb-1 text-sm font-semibold">
                Select Manager <span className="text-error">*</span>
              </label>
              <Select
                styles={customSelectStyles}
                options={managerSelectOptions}
                value={managerValueOption}
                isLoading={loadingUsers}
                placeholder="Select Manager"
                className="w-full"
                // classNames={{
                //   control: ({ isFocused }) => `!rounded-lg !border-gray-300 dark:!border-gray-600 !bg-gray-50 dark:!bg-gray-700 !min-h-[42px] ${isFocused ? '!ring-2 !ring-primary/20 !border-primary' : ''}`,
                //   placeholder: () => "!text-slate-400 dark:!text-gray-400 !text-sm",
                //   singleValue: () => "!text-slate-700 dark:!text-slate-200 !text-sm",
                //   menu: () => "!bg-white dark:!bg-slate-900 !border !border-slate-200 dark:!border-slate-700 !rounded-xl !shadow-xl !overflow-hidden",
                //   option: ({ isFocused, isSelected }) => `!text-sm ${isSelected ? '!bg-primary !text-white' : isFocused ? '!bg-primary/10 !text-primary' : '!text-slate-700 dark:!text-slate-300'}`
                // }}
                onChange={(val: any) => {
                  methods.setValue("manager", val?.value || "", { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                }}
                onMenuScrollToBottom={() => {
                  if (hasMoreUsers && !loadingUsers) void fetchUsers(userPage + 1, userSearch, true);
                }}
                onInputChange={(value, actionMeta) => {
                  if (actionMeta.action === "input-change") setUserSearch(value);
                  if (actionMeta.action === "menu-close") setUserSearch("");
                  return value;
                }}
              />
            </div>

              <Input compact name="clientContactName" label="Client Name" required onInput={(e: any) => {
                // 👇 Instantly remove special characters, + and -
                e.target.value = e.target.value.replace(/[^a-zA-Z0-9 ]/g, "");

                // Maintain your existing functionality
                handleNameInput(e, methods.setValue);
              }} />
            <PhoneNumberInput
              compact
              name="clientContactNumber"
              countryCodeName="clientContactCountryCode"
              label="Client Mobile Number"
              required
            />
            <Input compact name="startDate" label="Start Date" type="date" required />
              <Input compact name="completionDate" label="Completion Date" type="date" disabled={!watchedStartDate || !dirtyFields.startDate} />
          </div>

          {/* Employees */}
          <div className="px-4 mt-2">
            <label className="block mb-1 text-sm font-semibold">
              Select Employee <span className="text-error">*</span>
            </label>

            <Select
              styles={customSelectStyles}
              isMulti
              options={employeeSelectOptions}
              value={employeeValueOptions}
              isLoading={loadingUsers}
              placeholder="Select Employee"
              className="w-full"
              classNames={{
                control: ({ isFocused }) => `!rounded-lg !border-gray-300 dark:!border-gray-600 !bg-gray-50 dark:!bg-gray-700 !min-h-[42px] ${isFocused ? '!ring-2 !ring-primary/20 !border-primary' : ''}`,
                placeholder: () => "!text-slate-400 dark:!text-gray-400 !text-sm",
                multiValue: () => "!bg-primary/10 !text-primary !rounded-lg !border !border-primary/20",
                multiValueLabel: () => "!text-primary !text-xs !font-semibold",
                multiValueRemove: () => "!text-primary hover:!bg-primary/20 !rounded-r-lg",
                menu: () => "!bg-white dark:!bg-slate-900 !border !border-slate-200 dark:!border-slate-700 !rounded-xl !shadow-xl !overflow-hidden",
                option: ({ isFocused, isSelected }) => `!text-sm ${isSelected ? '!bg-primary !text-white' : isFocused ? '!bg-primary/10 !text-primary' : '!text-slate-700 dark:!text-slate-300'}`
              }}
              onChange={(vals: any) => {
                const value = (vals || []).map((v: any) => v.value).join(",");
                methods.setValue("emails", value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
              }}
              onMenuScrollToBottom={() => {
                if (hasMoreUsers && !loadingUsers) void fetchUsers(userPage + 1, userSearch, true);
              }}
              onInputChange={(value, actionMeta) => {
                if (actionMeta.action === "input-change") setUserSearch(value);
                if (actionMeta.action === "menu-close") setUserSearch("");
                return value;
              }}
            />

            {methods.formState.errors.emails && (
              <span className="text-error text-xs mt-1 block">
                {methods.formState.errors.emails.message as ReactNode}
              </span>
            )}
          </div>

          {/* Project Detail */}
          <div className="px-4 mt-1">
              <Input placeholder="Project Detail" compact name="projectDetail" label="Project Detail" type="textarea" required />
          </div>

          {/* Submit */}
          <div className="mt-3 flex justify-end px-3 gap-2">
            <Button
              type="button"
              onClick={onClose}
              className="!bg-white !text-slate-600 border border-slate-200 hover:!bg-slate-100 focus:!ring-4 focus:!outline-none focus:!ring-slate-200 transition-all active:scale-95"
            >
              Cancel
            </Button>
            <Button
              disabled={!isDirty || isSubmitting}
              type="submit"
              className="!text-white !bg-blue-700 hover:!bg-blue-800 focus:!ring-4 focus:!outline-none focus:!ring-blue-300 dark:!bg-blue-600 dark:hover:!bg-blue-700 dark:focus:!ring-blue-800"
            >
              Submit
            </Button>

          </div>
        </form>
      )}
    </FormProvider>
  );
}